import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
	Clock3,
	Copy,
	Flame,
	Frown,
	Laugh,
	Meh,
	RotateCcw,
	Smile,
} from "lucide-react";

const accentColor = "#16c784";

function clampPercent(value) {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.min(100, Math.max(0, value));
}

function formatElapsedTime(seconds = 0) {
	const safeSeconds = Math.max(0, Number(seconds) || 0);
	const hours = Math.floor(safeSeconds / 3600);
	const minutes = Math.floor((safeSeconds % 3600) / 60);
	const remainingSeconds = safeSeconds % 60;

	if (hours > 0) {
		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
	}

	return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function formatDateTime(value) {
	if (!value) {
		return "Sin programar";
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return "Sin programar";
	}

	return date.toLocaleString("es-MX", {
		day: "2-digit",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatMode(mode) {
	const modes = {
		due: "Due",
		new: "Nuevas",
		review: "Repaso",
		random: "Aleatorio",
	};

	return modes[mode] || mode || "-";
}

function formatRating(rating) {
	const ratings = {
		again: "Otra vez",
		hard: "Difícil",
		good: "Bien",
		easy: "Fácil",
	};

	return ratings[rating] || "Sin calificar";
}

function getRatingColor(rating) {
	const colors = {
		again: "#ef4444",
		hard: "#f59e0b",
		good: "#3b82f6",
		easy: "#16c784",
	};

	return colors[rating] || accentColor;
}

function getRatingIcon(rating) {
	const icons = {
		again: RotateCcw,
		hard: Frown,
		good: Smile,
		easy: Laugh,
	};

	return icons[rating] || Meh;
}

function InfoRow({ label, value, valueColor, badge = false }) {
	return (
		<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
			<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 500, lineHeight: badge ? "24px" : 1.2 }}>
				{label}
			</Typography>
			<Box
				component="span"
				sx={{
					alignItems: "center",
					border: badge ? "1px solid #dfe5ee" : "none",
					borderRadius: badge ? "999px" : 0,
					color: valueColor || "#0f172a",
					display: "inline-flex",
					fontSize: 13,
					fontWeight: 700,
					gap: 0.75,
					justifyContent: "center",
					lineHeight: 1,
					maxWidth: "62%",
					minHeight: badge ? 24 : "auto",
					px: badge ? 1.5 : 0,
					py: badge ? 0.5 : 0,
					textAlign: "right",
				}}
			>
				{value}
			</Box>
		</Stack>
	);
}

function Metric({ icon: Icon, label, value }) {
	return (
		<Stack direction="row" alignItems="flex-start" spacing={1.5}>
			<Box component="span" sx={{ color: "#64748b", display: "inline-flex", mt: 0.25 }}>
				<Icon size={20} strokeWidth={2} />
			</Box>
			<Box>
				<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>
					{label}
				</Typography>
				<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 800, mt: 0.25 }}>
					{value}
				</Typography>
			</Box>
		</Stack>
	);
}

export default function ProgresoFlashcards({ data, elapsedSeconds = 0 }) {
	const session = data?.session || {};
	const flashcard = data?.flashcard || {};
	const progress = data?.progress || {};
	const specialty = data?.specialty;
	const topic = data?.topic;

	const totalCards = Number(session.total_cards) || 0;
	const currentCard = Math.min(
		totalCards || 1,
		Number(flashcard.position) || Number(session.reviewed_cards) + 1 || 1
	);
	const progressPercent = clampPercent(totalCards > 0 ? (currentCard / totalCards) * 100 : 0);
	const successStreak = Number(session.correct_cards) || 0;
	const easeFactor = Number(progress.ease_factor || progress.easeFactor || 0);
	const lastRatingColor = getRatingColor(progress.last_rating);
	const LastRatingIcon = getRatingIcon(progress.last_rating);
	const cardId = flashcard.id || "-";
	const visibleCardId = cardId.length > 12 ? `${cardId.slice(0, 8)}...` : cardId;

	const handleCopyCardId = () => {
		if (cardId && cardId !== "-" && navigator?.clipboard) {
			navigator.clipboard.writeText(cardId);
		}
	};

	return (
		<Paper
			variant="outlined"
			sx={{
				borderColor: "#dfe5ee",
				borderRadius: "12px",
				boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
				p: { xs: 2.5, md: 3 },
				width: "100%",
			}}
		>
			<Typography sx={{ color: "#0f172a", fontSize: 16, fontWeight: 800, mb: 2 }}>
				Progreso de la sesión
			</Typography>

			<Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
				<Box sx={{ position: "relative", display: "inline-flex" }}>
					<CircularProgress
						variant="determinate"
						value={100}
						size={128}
						thickness={3.7}
						sx={{ color: "#eef2f7", position: "absolute" }}
					/>
					<CircularProgress
						variant="determinate"
						value={progressPercent}
						size={128}
						thickness={3.7}
						sx={{
							color: accentColor,
							"& .MuiCircularProgress-circle": { strokeLinecap: "butt" },
						}}
					/>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							flexDirection: "column",
							height: 128,
							inset: 0,
							justifyContent: "center",
							position: "absolute",
							width: 128,
						}}
					>
						<Typography sx={{ color: "#0f172a", fontSize: 26, fontWeight: 800, lineHeight: "26px" }}>
							{Math.round(progressPercent)}%
						</Typography>
						<Typography sx={{ color: "#475569", fontSize: 12, fontWeight: 700, lineHeight: "16px", mt: 0.75 }}>
							Completado
						</Typography>
					</Box>
				</Box>

				<Divider flexItem orientation="vertical" sx={{ display: { xs: "none", sm: "block" } }} />

				<Stack spacing={2.25} sx={{ flex: 1, width: "100%" }}>
					<Metric icon={Clock3} label="Tiempo en sesión" value={formatElapsedTime(elapsedSeconds)} />
					<Metric icon={Flame} label="Racha de aciertos" value={successStreak} />
				</Stack>
			</Stack>

			<Divider sx={{ my: 2.5 }} />

			<Typography sx={{ color: "#0f172a", fontSize: 16, fontWeight: 800, mb: 1.75 }}>
				Información de la tarjeta
			</Typography>

			<Stack spacing={1.05}>
				<InfoRow label="Modo" value={formatMode(session.mode)} badge />
				<InfoRow label="Materia" value={specialty?.name || "Sin materia"} />
				<InfoRow label="Tema" value={topic?.name || "Sin tema"} />
				<InfoRow label="Próxima revisión" value={formatDateTime(progress.due_at)} />
				<InfoRow label="Repeticiones" value={Number(progress.repetitions) || 0} />
				<InfoRow label="Ease factor" value={easeFactor > 0 ? easeFactor.toFixed(2) : "-"} />
				<InfoRow
					label="Última calificación"
					value={(
						<>
							<LastRatingIcon size={17} color={lastRatingColor} strokeWidth={2.3} />
							{formatRating(progress.last_rating)}
						</>
					)}
					valueColor={lastRatingColor}
				/>
			</Stack>

			<Divider sx={{ my: 2 }} />

			<Stack spacing={1.05}>
				<InfoRow label="Creada" value={flashcard.created_at ? formatDateTime(flashcard.created_at) : "-"} />
				<InfoRow label="Fuente" value={flashcard.source_type || "Manual"} />
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
					<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>
						ID de tarjeta
					</Typography>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 700 }}>
							{visibleCardId}
						</Typography>
						<IconButton size="small" onClick={handleCopyCardId} sx={{ color: "#64748b", p: 0.25 }}>
							<Copy size={16} />
						</IconButton>
					</Stack>
				</Stack>
			</Stack>
		</Paper>
	);
}
